export type WidgetType =
  | 'forward-returns'
  | 'asset-breakdown'
  | 'correlation-cluster'
  | 'historical-returns'
  | 'sector-allocation';

interface PopoutParams {
  widgetType: WidgetType;
  portfolioId?: string;
  snapshotId?: string;
  scenario?: 'PROPOSED' | 'CURRENT_HOLDINGS' | 'BENCHMARK';
  benchmarkName?: string;
  title?: string;
  horizonDays?: number;
  lookbackDays?: number;
  correlationThreshold?: number;
  portfolioType?: 'current' | 'proposed';
  metricType?: 'drawdown' | 'beta';
  portfolioName?: string;
}

export const generatePopoutUrl = (params: PopoutParams): string => {
  const {
    widgetType,
    portfolioId,
    snapshotId,
    scenario,
    benchmarkName,
    title,
    horizonDays,
    lookbackDays,
    correlationThreshold,
    portfolioType,
    metricType,
    portfolioName,
  } = params;

  const baseUrl = `${window.location.origin}/popout/${encodeURIComponent(
    widgetType,
  )}`;

  const searchParams = new URLSearchParams();
  if (portfolioId) searchParams.append('portfolioId', portfolioId);
  if (snapshotId) searchParams.append('snapshotId', snapshotId);
  if (scenario) searchParams.append('scenario', scenario);
  if (benchmarkName) searchParams.append('benchmarkName', benchmarkName);
  if (title) searchParams.append('title', title);
  if (horizonDays) searchParams.append('horizonDays', String(horizonDays));
  if (lookbackDays) searchParams.append('lookbackDays', String(lookbackDays));
  if (correlationThreshold)
    searchParams.append('correlationThreshold', String(correlationThreshold));
  if (portfolioType) searchParams.append('portfolioType', portfolioType);
  if (metricType) searchParams.append('metricType', metricType);
  if (portfolioName) searchParams.append('portfolioName', portfolioName);

  const qs = searchParams.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
};

export const openPopoutWindow = (
  params: PopoutParams,
  width = 1400,
  height = 900,
): void => {
  const url = generatePopoutUrl(params);

  // center window (use fallbacks for different browsers)
  const screenX =
    typeof window.screenX !== 'undefined' ? window.screenX : window.screenLeft;
  const screenY =
    typeof window.screenY !== 'undefined' ? window.screenY : window.screenTop;
  const outerWidth =
    typeof window.outerWidth !== 'undefined'
      ? window.outerWidth
      : document.documentElement.clientWidth;
  const outerHeight =
    typeof window.outerHeight !== 'undefined'
      ? window.outerHeight
      : document.documentElement.clientHeight;

  const left = Math.round(screenX + (outerWidth - width) / 2);
  const top = Math.round(screenY + (outerHeight - height) / 2);

  const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  const name = `popout-${params.widgetType}`;

  const newWindow = window.open(url, name, features);
  // focus if possible
  try {
    newWindow?.focus();
  } catch {
    // ignore if cross-origin or blocked
  }
};
